import { useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import GalleryService from '@/services/gallery.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import OrderCard from '@/shared-components/cards/components/OrderCard';
import TimeLeft from '@/shared-components/common/components/TimeLeft';
import AddShippingAddress from '@/shared-components/forms/components/AddShippingAddress';
import ForwardArrowIcon from '@/shared-components/icons/components/public/ForwardArrowIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';

import './styles/AddShippingDetailsCard.css';

export default function AddShippingDetailsCard({ orderData, onAddShippingDetails }) {
  const t = useTranslations('common.shared.order_details.shipping');
  const [openCollectorShipping, setOpenCollectorShipping] = useState(false);
  const [formState, setFormState] = useState({
    tracking_number: "",
    shipping_courier_company: ""
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  async function submitShippingDetails(e) {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const { response, data } = await GalleryService.submitShippingDetails(orderData.order_number, formState);
      if (response.ok) {
        onAddShippingDetails(orderData._id);
        return;
      }
      setFormError({
        element: data?.error_data?.element ?? "tracking_number",
        error_code: data?.error_type ?? "REQUEST_FAILED",
      });
    } catch (_error) {
      setFormError({ element: "tracking_number", error_code: "REQUEST_FAILED" });
    } finally {
      setFormLoading(false);
    }
  }

  function getTimeLeft() {
    const initialTimestamp = new Date(orderData.captured_at ?? orderData.createdAt).getTime();
    const offset = (7 * 24 * 60 * 60 * 1000);

    return new Date(initialTimestamp + offset).getTime();
  }

  function getTimeLeftLabel(timeLeft) {
    if (Math.abs(timeLeft.days) > 10) {
      return t('time_left_days', { days: timeLeft.days });
    }

    return t('time_left', timeLeft);
  }

  return (
    <>
      <OrderCard orderData={orderData} navigateToView>
        <div className="add-shipping-details-card__utility-row">
          <span className="add-shipping-details-card__button element-non-selectable" onClick={()=>setOpenCollectorShipping(true)}>
            {t('view_shipping')}

            <ForwardArrowIcon className="add-shipping-details-card__button-enter"/>
          </span>

          <TimeLeft endsIn={getTimeLeft()}>
            {({ timeLeft })=>(
              <span className="add-shipping-details-card__time-left">
                {getTimeLeftLabel(timeLeft)}
              </span>
            )}
          </TimeLeft>
        </div>

        <form onSubmit={submitShippingDetails} className="add-shipping-details-card__form">
          <div className="add-shipping-details-card__fields">
            <PublicFormInput 
              value={formState.tracking_number} 
              onChange={e=>setFormState({ ...formState, tracking_number: e.target.value })} 
              readOnly={formLoading} 
              error={formError} 
              placeholder={t('tracking_number')}
              element="tracking_number" 
            />

            <PublicFormInput 
              value={formState.shipping_courier_company} 
              onChange={e=>setFormState({ ...formState, shipping_courier_company: e.target.value })} 
              readOnly={formLoading} 
              error={formError} 
              placeholder={t('courier')}
              element="shipping_courier_company" 
            />
          </div>

          <div className="add-shipping-details-card__submit-section">
            <ContinueButton
              className="add-shipping-details-card__submit-button"
              turnOff={!formState.tracking_number || !formState.shipping_courier_company}
              loading={formLoading}
            >
              {t('submit_shipping')}
            </ContinueButton>
          </div>
        </form>
      </OrderCard>

      <AddShippingAddress
        open={openCollectorShipping}
        close={()=>setOpenCollectorShipping(false)}
        addressData={orderData.order_shipping_address}
        previewMode
      />
    </>
  );
}
